import { useEffect, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { CashSessionsAdminService } from "../../services";
import { currency, dateTime } from "../../utils/format";
import { currentBusinessDate, isInBusinessDay } from "../../utils/businessDay";
import { Input } from "@/components/ui/input";

export const AdminCashSessionsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [businessDate, setBusinessDate] = useState(currentBusinessDate());
  const [outletCode, setOutletCode] = useState("ALL");

  useEffect(() => {
    setLoading(true);
    CashSessionsAdminService.list(businessDate, outletCode)
      .then(setRows)
      .finally(() => setLoading(false));
  }, [businessDate, outletCode]);

  const filtered = rows.filter((r) => {
    const matchDate = isInBusinessDay(r.openedAt, businessDate);

    const cashier = `${r.cashierName || ""} ${r.cashierFullName || ""} ${r.cashierUsername || ""}`.toLowerCase();
    const status = String(r.status || "").toLowerCase();
    const outlet = String(r.outletName || r.outletCode || "").toLowerCase();
    const searchText = q.toLowerCase().trim();

    const matchSearch =
      !searchText ||
      cashier.includes(searchText) ||
      status.includes(searchText) ||
      outlet.includes(searchText);

    return matchDate && matchSearch;
  });

  return (
    <div>
      <PageHeader
        title="Cash Sessions"
        subtitle="Open and closed cashier sessions with expected vs actual cash."
      />

      <div className="flex flex-wrap gap-3 mb-4">
        <Input
          placeholder="Search sessions by cashier or status…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
          data-testid="cash-session-search"
        />

        <select
          aria-label="Filter cash sessions by outlet"
          value={outletCode}
          onChange={(e) => setOutletCode(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
          data-testid="cash-session-outlet"
        >
          <option value="ALL">All outlets</option>
          <option value="BAR">BAR</option>
          <option value="RESTAURANT">RESTAURANT</option>
        </select>

        <Input
          type="date"
          value={businessDate}
          onChange={(e) => setBusinessDate(e.target.value)}
          className="w-44"
          data-testid="cash-session-business-date"
        />
      </div>

      <DataTable
        paginationResetKey={`${businessDate}|${outletCode}|${q}`}
        loading={loading}
        rows={filtered.map((r) => ({ ...r, key: r.cashSessionId }))}
        empty="No cash sessions found."
        testId="admin-sessions-table"
        columns={[
          { key: "cashSessionId", header: "ID" },
          {
            key: "cashierName",
            header: "Cashier",
            render: (r) =>
              r.cashierName ||
              r.cashierFullName ||
              r.cashierUsername ||
              "—",
          },
          {
            key: "outletCode",
            header: "Outlet",
            render: (r) => r.outletName || r.outletCode || "—",
          },
          {
            key: "openedAt",
            header: "Opened",
            render: (r) => dateTime(r.openedAt),
          },
          {
            key: "closedAt",
            header: "Closed",
            render: (r) => (r.closedAt ? dateTime(r.closedAt) : "—"),
          },
          {
            key: "openingCashAmount",
            header: "Opening",
            render: (r) => currency(r.openingCashAmount),
          },
          {
            key: "cashSalesAmount",
            header: "Cash sales",
            render: (r) => currency(r.cashSalesAmount),
          },
          {
            key: "expectedClosingCash",
            header: "Expected",
            render: (r) => currency(r.expectedClosingCash),
          },
          {
            key: "actualClosingCash",
            header: "Actual",
            render: (r) =>
              r.actualClosingCash != null
                ? currency(r.actualClosingCash)
                : "—",
          },
          {
            key: "cashDifference",
            header: "Diff",
            render: (r) =>
              r.cashDifference == null ? (
                "—"
              ) : (
                <span
                  className={
                    r.cashDifference < 0
                      ? "text-rose-600"
                      : "text-emerald-600"
                  }
                >
                  {currency(r.cashDifference)}
                </span>
              ),
          },
          {
            key: "status",
            header: "Status",
            render: (r) => <StatusBadge value={r.status} />,
          },
        ]}
      />
    </div>
  );
};
